'use client';

import { useCallback, useEffect, useRef } from 'react';
import axios from 'axios';
import useTimerStore from '@/store/timeTrackStore';
import { useRouter } from 'next/router';

const IDLE_TIMEOUT_MS = 300000;

const formatDuration = (seconds: number) => {
  const h = String(Math.floor(seconds / 3600)).padStart(2, '0');
  const m = String(Math.floor((seconds % 3600) / 60)).padStart(2, '0');
  const s = String(seconds % 60).padStart(2, '0');
  return `${h}:${m}:${s}`;
};

const pathnameFromUrl = (url: string) => {
  if (url.startsWith('http')) {
    return new URL(url).pathname;
  }
  return url.split('?')[0].split('#')[0];
};

const extractProjectIdFromUrl = (url: string) => {
  const pathname = pathnameFromUrl(url);
  const patterns = [
    /^\/projects-v2\/(\d+)/,
    /^\/projectsv2\/projects\/(\d+)/,
    /^\/projectsv2\/(\d+)/,
    /^\/projects\/(\d+)/,
    /^\/sf\/projects\/(\d+)/,
    /^\/settlefast\/projects\/(\d+)/,
  ];
  for (const pattern of patterns) {
    const match = pathname.match(pattern);
    if (match?.[1]) {
      return match[1];
    }
  }
  return null;
};

const sectionKeyFromUrl = (url: string) => {
  const pathname = pathnameFromUrl(url);
  if (pathname === '/projects-v2/new') return 'new-project';
  if (pathname.includes('/upload')) return 'uploads';
  if (pathname.includes('/pair')) return 'pair-items';
  if (pathname.includes('/organize')) return 'organize-items';
  if (pathname.includes('/setup')) return 'project-setup';
  if (pathname.includes('/selections')) return 'selections';
  if (pathname.includes('/initial-summary') || pathname.includes('/initial-dispute')) {
    return 'initial-summary';
  }
  if (pathname.includes('/final-summary')) return 'final-summary';
  if (pathname.includes('/final-release')) return 'final-release';
  if (pathname.includes('/final-award')) return 'final-award';
  if (pathname.includes('/final-invoice')) return 'final-invoice';
  if (pathname.includes('/final-outcome')) return 'final-outcome';
  if (pathname.includes('/numbers')) return 'numbers';
  if (extractProjectIdFromUrl(pathname)) return 'project';
  return 'other';
};

const isPreProjectNewRoute = (url: string) => pathnameFromUrl(url) === '/projects-v2/new';

const buildSectionNotes = (sections: Record<string, number>) => {
  const entries = Object.entries(sections)
    .filter(([, value]) => Number.isFinite(value) && value > 0)
    .map(([key, value]) => `${key}=${Math.floor(value)}`);
  const summary = entries.length > 0 ? entries.join('; ') : 'none';
  const notes = `sections: ${summary}`;
  return notes.slice(0, 255);
};

const generateSessionId = () => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `session-${Math.random().toString(36).slice(2, 10)}-${Date.now()}`;
};

export default function Timer() {
  const {
    activeTime,
    isTracking,
    projectId,
    startTime,
    sessionProjectId,
    sessionId,
    isPaused,
    pausedAt,
    lastActivityAt,
    sections,
    currentSectionKey,
    setActiveTime,
    setIsTracking,
    setStartTime,
    setSessionProjectId,
    setSessionId,
    setIsPaused,
    setPausedAt,
    setLastActivityAt,
    setCurrentSectionKey,
    incrementSectionSecond,
    resetSession,
  } = useTimerStore();
  const router = useRouter();
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const idleTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const recordInFlightRef = useRef(false);
  const lastManualStopAtRef = useRef(0);

  const clearIntervalTimer = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  const clearIdleTimeout = useCallback(() => {
    if (idleTimeoutRef.current) {
      clearTimeout(idleTimeoutRef.current);
      idleTimeoutRef.current = null;
    }
  }, []);

  const scheduleIdleTimeout = useCallback(() => {
    clearIdleTimeout();
    if (!isTracking || typeof window === 'undefined') return;
    idleTimeoutRef.current = setTimeout(() => {
      clearIntervalTimer();
      setIsTracking(false);
      setIsPaused(true);
      setPausedAt(new Date().toISOString());
    }, IDLE_TIMEOUT_MS);
  }, [clearIdleTimeout, clearIntervalTimer, isTracking, setIsPaused, setIsTracking, setPausedAt]);

  const stopAndRecord = useCallback(
    async (reason?: 'manual' | 'route' | 'idle' | 'unload') => {
      const hasSession = !!startTime && (isTracking || isPaused || activeTime > 0);
      if (!hasSession) {
        return;
      }

      clearIntervalTimer();
      clearIdleTimeout();

      if (activeTime < 1 || !startTime) {
        resetSession();
        return;
      }

      const recordProjectId = sessionProjectId ?? extractProjectIdFromUrl(router.asPath);
      if (!recordProjectId) {
        resetSession();
        return;
      }

      if (recordInFlightRef.current) return;
      recordInFlightRef.current = true;

      const nowIso = new Date().toISOString();
      const payload = {
        projectId: recordProjectId,
        action: 'record',
        projectTimeTracker: {
          time: activeTime,
          startTime,
          sessionId: sessionId ?? null,
          notes: buildSectionNotes(sections),
          sectionsJson: JSON.stringify(sections),
          lastActivityAt: lastActivityAt ?? nowIso,
          pausedAt: pausedAt ?? (isPaused ? nowIso : null),
        },
      };

      try {
        if (reason === 'unload') {
          const blob = new Blob([JSON.stringify(payload)], { type: 'application/json' });
          navigator.sendBeacon('/api/projects/timeTracker', blob);
        } else {
          await axios.post('/api/projects/timeTracker', payload);
          window.dispatchEvent(new Event('time-tracker-recorded'));
        }
      } catch (err) {
        console.error('time track error', err);
      } finally {
        recordInFlightRef.current = false;
      }

      resetSession();
    },
    [
      activeTime,
      clearIdleTimeout,
      clearIntervalTimer,
      isPaused,
      isTracking,
      lastActivityAt,
      pausedAt,
      resetSession,
      router.asPath,
      sections,
      sessionId,
      sessionProjectId,
      startTime,
    ],
  );

  const startTrackingIfPossible = useCallback(() => {
    if (typeof window === 'undefined') return;
    const routeProjectId = extractProjectIdFromUrl(router.asPath);
    const effectiveProjectId = projectId ?? routeProjectId;
    const onPreProjectRoute = isPreProjectNewRoute(router.asPath);

    if (!effectiveProjectId && !onPreProjectRoute) return;

    if (startTime && (isTracking || isPaused)) {
      scheduleIdleTimeout();
      return;
    }

    const nowIso = new Date().toISOString();
    if (!startTime) {
      setActiveTime(0);
      setStartTime(nowIso);
      setSessionId(generateSessionId());
      setIsPaused(false);
      setPausedAt(null);
      setSessionProjectId(effectiveProjectId ?? null);
    }

    setIsTracking(true);
    setLastActivityAt(nowIso);
    scheduleIdleTimeout();
  }, [
    isPaused,
    isTracking,
    projectId,
    router.asPath,
    scheduleIdleTimeout,
    setActiveTime,
    setIsPaused,
    setIsTracking,
    setLastActivityAt,
    setPausedAt,
    setSessionId,
    setSessionProjectId,
    setStartTime,
    startTime,
  ]);

  useEffect(() => {
    setCurrentSectionKey(sectionKeyFromUrl(router.asPath));
  }, [router.asPath, setCurrentSectionKey]);

  useEffect(() => {
    if (!isTracking) {
      clearIntervalTimer();
      return;
    }
    intervalRef.current = setInterval(() => {
      setActiveTime((t) => t + 1);
      incrementSectionSecond(currentSectionKey || 'unknown');
    }, 1000);
    return () => {
      clearIntervalTimer();
    };
  }, [clearIntervalTimer, currentSectionKey, incrementSectionSecond, isTracking, setActiveTime]);

  const handleToggle = async () => {
    if (isTracking || isPaused) {
      lastManualStopAtRef.current = Date.now();
      await stopAndRecord('manual');
    } else {
      startTrackingIfPossible();
    }
  };

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const handlePointerDown = (event: PointerEvent) => {
      if (Date.now() - lastManualStopAtRef.current < 500) return;
      if (event.target instanceof Element) {
        if (event.target.closest('[data-timer-root="true"]')) return;
      }

      const nowIso = new Date().toISOString();
      setLastActivityAt(nowIso);

      if (startTime && isPaused) {
        setIsPaused(false);
        setPausedAt(null);
        setIsTracking(true);
        scheduleIdleTimeout();
        return;
      }

      if (isTracking) {
        scheduleIdleTimeout();
        return;
      }

      startTrackingIfPossible();
    };
    window.addEventListener('pointerdown', handlePointerDown, { passive: true });
    return () => {
      window.removeEventListener('pointerdown', handlePointerDown);
    };
  }, [
    isPaused,
    isTracking,
    scheduleIdleTimeout,
    setIsPaused,
    setIsTracking,
    setLastActivityAt,
    setPausedAt,
    startTime,
    startTrackingIfPossible,
  ]);

  useEffect(() => {
    const handleRouteStart = (nextUrl: string) => {
      const currentSessionProjectId =
        sessionProjectId ?? extractProjectIdFromUrl(router.asPath);
      const nextProjectId = extractProjectIdFromUrl(nextUrl);
      const currentIsNew = isPreProjectNewRoute(router.asPath);
      const hasSession = !!startTime && (isTracking || isPaused || activeTime > 0);

      if (!hasSession) return;

      if (
        currentSessionProjectId &&
        nextProjectId &&
        nextProjectId === currentSessionProjectId
      ) {
        return;
      }

      if (!currentSessionProjectId && currentIsNew && nextProjectId) {
        setSessionProjectId(nextProjectId);
        return;
      }

      if (currentSessionProjectId && (!nextProjectId || nextProjectId !== currentSessionProjectId)) {
        void stopAndRecord('route');
        return;
      }

      if (currentIsNew && isPreProjectNewRoute(nextUrl)) {
        return;
      }
    };

    router.events.on('routeChangeStart', handleRouteStart);
    return () => {
      router.events.off('routeChangeStart', handleRouteStart);
    };
  }, [
    activeTime,
    isPaused,
    isTracking,
    router.asPath,
    router.events,
    sessionProjectId,
    setSessionProjectId,
    startTime,
    stopAndRecord,
  ]);

  useEffect(() => {
    const handleRouteComplete = (completedUrl: string) => {
      setCurrentSectionKey(sectionKeyFromUrl(completedUrl));
      if (!sessionProjectId && startTime) {
        const nextProjectId = extractProjectIdFromUrl(completedUrl);
        if (nextProjectId) {
          setSessionProjectId(nextProjectId);
        }
      }
      if (Date.now() - lastManualStopAtRef.current < 500) return;
      startTrackingIfPossible();
    };

    router.events.on('routeChangeComplete', handleRouteComplete);
    return () => {
      router.events.off('routeChangeComplete', handleRouteComplete);
    };
  }, [
    router.events,
    sessionProjectId,
    setCurrentSectionKey,
    setSessionProjectId,
    startTime,
    startTrackingIfPossible,
  ]);

  useEffect(() => {
    const handleUnload = () => {
      if (isTracking || isPaused) {
        stopAndRecord('unload');
      }
    };
    window.addEventListener('beforeunload', handleUnload);
    return () => {
      window.removeEventListener('beforeunload', handleUnload);
    };
  }, [isPaused, isTracking, stopAndRecord]);

  useEffect(() => {
    if (typeof document === 'undefined') return;

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden' && isTracking) {
        clearIntervalTimer();
        clearIdleTimeout();
        setIsTracking(false);
        setIsPaused(true);
        setPausedAt(new Date().toISOString());
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [clearIdleTimeout, clearIntervalTimer, isTracking, setIsPaused, setIsTracking, setPausedAt]);

  useEffect(() => {
    return () => {
      clearIntervalTimer();
      clearIdleTimeout();
    };
  }, [clearIdleTimeout, clearIntervalTimer]);

  return (
    <div className="flex items-center mr-2" data-timer-root="true">
      <span className="font-mono text-sm mr-1">{formatDuration(activeTime)}</span>
      <button
        onClick={handleToggle}
        className="px-1 py-0.5 text-xs border rounded"
      >
        {isTracking || isPaused ? 'Stop' : 'Start'}
      </button>
    </div>
  );
}
